import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'
import { IFuelTankEconomics } from 'types/fuelReconciliation.interface'
import {
  IFuelBowserDayDispenseRow,
  IFuelBowserReconComputed,
  IFuelBowserReconDay,
  IFuelContractorRechargeRow,
  IFuelCustodyReconComputed,
  IFuelCustodyReconDay,
  IFuelCustodyTankDemo,
  IFuelDispenserRow,
  IFuelHousekeepingRow,
  IFuelMonthEndClose,
  IFuelMonthEndTankRow,
  IFuelTransferLine,
  IFuelWaterCheckMap,
} from 'types/fuelCustody.interface'

export const FUEL_DIESEL_PRICE_GHS = 12.4

export const FUEL_COVER_DANGER_DAYS = 3

export const FUEL_COVER_WARNING_DAYS = 7

export const FUEL_VARIANCE_TOLERANCE_PCT = 0.5

export const FUEL_COVER_THRESHOLD_TITLE =
  'Days of cover = current level ÷ burn rate. Reorder below 3 days · watch below 7 days · Ok at 7 or more.'

export const FUEL_THRESHOLD_FOOTNOTE =
  'Cover thresholds: reorder <3 days · watch <7 days · tolerance ±0.5% — configured per site during onboarding.'

export const FUEL_BOWSER_TOLERANCE_PCT = 1.5

export const DEMO_FUEL_TANKS: IFuelCustodyTankDemo[] = [
  { id: 9001, name: 'Pit A Diesel', currentAmount: 3600, capacity: 12000, burnRatePerDay: 1850, pricePerLitre: FUEL_DIESEL_PRICE_GHS, currentSensorAmount: null, machineClass: 'STATIC' },
  { id: 9002, name: 'Pit B Diesel', currentAmount: 8000, capacity: 8000, burnRatePerDay: null, pricePerLitre: FUEL_DIESEL_PRICE_GHS, currentSensorAmount: null, machineClass: 'STATIC' },
  { id: 9003, name: 'Workshop', currentAmount: 14500, capacity: 20000, burnRatePerDay: 950, pricePerLitre: FUEL_DIESEL_PRICE_GHS, currentSensorAmount: 14380, machineClass: 'STATIC' },
  { id: 9004, name: 'Plant', currentAmount: 12354, capacity: 16000, burnRatePerDay: 1050, pricePerLitre: FUEL_DIESEL_PRICE_GHS, currentSensorAmount: 11894, machineClass: 'STATIC' },
  { id: 9005, name: 'Camp genset', currentAmount: 8500, capacity: 10000, burnRatePerDay: 250, pricePerLitre: FUEL_DIESEL_PRICE_GHS, currentSensorAmount: null, machineClass: 'STATIC' },
]

export const DEMO_FUEL_BOWSER: IFuelCustodyTankDemo = {
  id: 9101,
  name: 'BOWSER-01',
  currentAmount: 310,
  capacity: 10000,
  burnRatePerDay: null,
  pricePerLitre: FUEL_DIESEL_PRICE_GHS,
  currentSensorAmount: null,
  machineClass: 'BOWSER',
  routeMeta: 'mobile · serves Pit A + Pit B circuits',
  coverNote: 'reloads daily · Workshop tank',
}

export const FUEL_CUSTODY_TANKS: IFuelCustodyTankDemo[] = [...DEMO_FUEL_TANKS, DEMO_FUEL_BOWSER]

export const fuelTankEconomicsOf = (name: string): IFuelTankEconomics => {
  const match = DEMO_FUEL_TANKS.find((tank) => tank.name.toLowerCase() === (name || '').toLowerCase())
  return match
    ? { burnRatePerDay: match.burnRatePerDay, pricePerLitre: match.pricePerLitre }
    : { burnRatePerDay: null, pricePerLitre: FUEL_DIESEL_PRICE_GHS }
}

export const fuelReconDateISO = (daysAgo: number): string => format(subDays(new Date(), daysAgo), 'yyyy-MM-dd')

export const fuelReconDateLabel = (iso: string): string => {
  const date = parseISO(iso)
  const diff = differenceInCalendarDays(new Date(), date)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return format(date, 'd MMM')
}

const rd = (
  tankId: number,
  daysAgo: number,
  openingDipL: number,
  deliveriesL: number,
  dispensesL: number,
  dispenseCount: number,
  closingDipL: number,
  transfers?: IFuelTransferLine[],
): IFuelCustodyReconDay => ({
  tankId,
  dateISO: fuelReconDateISO(daysAgo),
  openingDipL,
  deliveriesL,
  dispensesL,
  dispenseCount,
  closingDipL,
  transfers,
})

const tl = (
  id: number,
  direction: 'TRANSFER_IN' | 'TRANSFER_OUT',
  counterparty: string,
  litres: number,
  time: string,
  label: string,
): IFuelTransferLine => ({ id, direction, counterparty, litres, time, label })

export const FUEL_RECON_DAYS: IFuelCustodyReconDay[] = [
  rd(9001, 7, 6850, 2000, 1865, 13, 6970),
  rd(9001, 6, 6970, 0, 1880, 13, 5080),
  rd(9001, 5, 5080, 0, 1790, 12, 2980),
  rd(9001, 4, 2980, 4500, 1820, 12, 5650),
  rd(9001, 3, 5650, 0, 1795, 12, 3850),
  rd(9001, 2, 3850, 3400, 1800, 12, 5450),
  rd(9001, 1, 5450, 0, 4830, 12, 3600, [
    tl(1, 'TRANSFER_IN', 'Workshop', 3000, '06:10', 'Transfer in — from Workshop'),
  ]),
  rd(9002, 7, 5020, 0, 0, 0, 5010),
  rd(9002, 6, 5010, 0, 0, 0, 5010),
  rd(9002, 5, 5010, 0, 0, 0, 5000),
  rd(9002, 4, 5000, 0, 0, 0, 5000),
  rd(9002, 3, 5000, 0, 0, 0, 4990),
  rd(9002, 2, 4990, 3000, 0, 0, 8000),
  rd(9002, 1, 8000, 0, 0, 0, 8000),
  rd(9003, 7, 17175, 0, 955, 7, 16220),
  rd(9003, 6, 16220, 4000, 950, 7, 19270),
  rd(9003, 5, 19270, 0, 950, 7, 18320),
  rd(9003, 4, 18320, 0, 965, 8, 17350),
  rd(9003, 3, 17350, 0, 945, 7, 16400),
  rd(9003, 2, 16400, 0, 950, 7, 15450),
  rd(9003, 1, 15450, 11000, 945, 7, 14500, [
    tl(2, 'TRANSFER_OUT', 'BOWSER-01', 8000, '05:40', 'Transfer out — BOWSER-01 load'),
    tl(3, 'TRANSFER_OUT', 'Pit A Diesel', 3000, '06:10', 'Transfer out — Pit A Diesel'),
  ]),
  rd(9004, 7, 14180, 0, 1055, 9, 13125),
  rd(9004, 6, 13125, 0, 1050, 9, 12070),
  rd(9004, 5, 12070, 0, 1050, 9, 11020),
  rd(9004, 4, 11020, 5500, 1045, 9, 15520),
  rd(9004, 3, 15520, 0, 1055, 9, 14460),
  rd(9004, 2, 14460, 0, 1050, 9, 13410),
  rd(9004, 1, 13410, 0, 1050, 9, 12354),
  rd(9005, 7, 6240, 4000, 240, 2, 10000),
  rd(9005, 6, 10000, 0, 240, 2, 9760),
  rd(9005, 5, 9760, 0, 255, 2, 9505),
  rd(9005, 4, 9505, 0, 250, 2, 9255),
  rd(9005, 3, 9255, 0, 250, 2, 9002),
  rd(9005, 2, 9002, 0, 250, 2, 8752),
  rd(9005, 1, 8752, 0, 250, 2, 8500),
]

export const computeFuelRecon = (day: IFuelCustodyReconDay): IFuelCustodyReconComputed => {
  const transfers = day.transfers ?? []
  const transfersInL = transfers
    .filter((line) => line.direction === 'TRANSFER_IN')
    .reduce((sum, line) => sum + line.litres, 0)
  const transfersOutL = transfers
    .filter((line) => line.direction === 'TRANSFER_OUT')
    .reduce((sum, line) => sum + line.litres, 0)
  const bookStockL = day.openingDipL + day.deliveriesL + transfersInL - transfersOutL - day.dispensesL
  const varianceL = day.closingDipL - bookStockL
  const denominator = day.openingDipL + day.deliveriesL + transfersInL
  const rawPct = denominator > 0 ? (varianceL / denominator) * 100 : 0
  const rounded = Math.round(rawPct * 10) / 10
  const variancePct = rounded === 0 ? 0 : rounded
  return {
    ...day,
    transfers,
    transfersInL,
    transfersOutL,
    bookStockL,
    varianceL,
    variancePct,
    withinTolerance: Math.abs(variancePct) <= FUEL_VARIANCE_TOLERANCE_PCT,
  }
}

const brd = (
  daysAgo: number,
  loadedL: number,
  loadedTime: string,
  dispensedL: number,
  machineCount: number,
  meteredReturnL: number,
  note?: string,
): IFuelBowserReconDay => ({
  bowserId: DEMO_FUEL_BOWSER.id,
  dateISO: fuelReconDateISO(daysAgo),
  loadedL,
  loadedFrom: 'Workshop',
  loadedTime,
  dispensedL,
  machineCount,
  meteredReturnL,
  note,
})

export const FUEL_BOWSER_RECON_DAYS: IFuelBowserReconDay[] = [
  brd(7, 7800, '05:35', 7430, 9, 340),
  brd(6, 8200, '05:45', 7890, 10, 280),
  brd(5, 7400, '05:40', 7050, 9, 320),
  brd(4, 7600, '05:50', 7150, 9, 130, 'meter drift suspected — see dispenser calibration'),
  brd(3, 8000, '05:40', 7610, 9, 350),
  brd(2, 7900, '05:35', 7520, 9, 340),
  brd(1, 8000, '05:40', 7620, 9, 310),
]

export const FUEL_BOWSER_DAY_DETAIL: IFuelBowserDayDispenseRow[] = [
  { id: 1, machine: 'Truck 05', time: '06:20', litres: 1380 },
  { id: 2, machine: 'Truck 12', time: '06:45', litres: 1290 },
  { id: 3, machine: 'EXC-01', time: '07:10', litres: 640 },
  { id: 4, machine: 'Truck 07', time: '07:50', litres: 1240 },
  { id: 5, machine: 'DOZ-02', time: '08:25', litres: 410 },
  { id: 6, machine: 'WT-01', time: '09:40', litres: 260 },
  { id: 7, machine: 'EXC-02', time: '10:55', litres: 980 },
  { id: 8, machine: 'DRILL-01', time: '13:05', litres: 760 },
  { id: 9, machine: 'GRADER-01', time: '15:20', litres: 660 },
]

export const FUEL_BOWSER_DAY_DETAIL_NOTE =
  'Every bowser litre lands in the dispense log with BOWSER-01 as source.'

export const computeBowserRecon = (day: IFuelBowserReconDay): IFuelBowserReconComputed => {
  const expectedReturnL = day.loadedL - day.dispensedL
  const varianceL = day.meteredReturnL - expectedReturnL
  const rawPct = day.loadedL > 0 ? (varianceL / day.loadedL) * 100 : 0
  const rounded = Math.round(rawPct * 10) / 10
  const variancePct = rounded === 0 ? 0 : rounded
  return {
    ...day,
    expectedReturnL,
    varianceL,
    variancePct,
    withinTolerance: Math.abs(variancePct) <= FUEL_BOWSER_TOLERANCE_PCT,
  }
}

export const FUEL_TANK_WATER_CHECKS: IFuelWaterCheckMap = {
  9001: { dateLabel: '1 Jul', result: 'clear', warning: false },
  9002: { dateLabel: '1 Jul', result: 'clear', warning: false },
  9003: { dateLabel: '1 Jul', result: 'clear', warning: false },
  9004: { dateLabel: '1 Jul', result: 'trace — recheck booked', warning: true },
  9005: { dateLabel: '1 Jul', result: 'clear', warning: false },
  9101: { dateLabel: '1 Jul', result: 'clear', warning: false },
}

export const FUEL_HOUSEKEEPING_ROWS: IFuelHousekeepingRow[] = [
  { id: 9001, tank: 'Pit A Diesel', lastCleaned: '8 Feb', nextDue: '8 Aug', waterHistory: 'clear · clear · clear', statusTone: 'success', statusLabel: 'Ok' },
  { id: 9002, tank: 'Pit B Diesel', lastCleaned: '20 Dec 2025', nextDue: '20 Jun', waterHistory: 'clear · clear · clear', statusTone: 'danger', statusLabel: 'Cleaning overdue' },
  { id: 9003, tank: 'Workshop', lastCleaned: '15 Mar', nextDue: '15 Sep', waterHistory: 'clear · clear · clear', statusTone: 'success', statusLabel: 'Ok' },
  { id: 9004, tank: 'Plant', lastCleaned: '2 Apr', nextDue: '2 Oct', waterHistory: 'clear · clear · trace', statusTone: 'success', statusLabel: 'Ok' },
  { id: 9005, tank: 'Camp genset', lastCleaned: '12 May', nextDue: '12 Aug', waterHistory: 'clear · clear · clear', statusTone: 'success', statusLabel: 'Ok' },
  { id: 9101, tank: 'BOWSER-01', lastCleaned: '5 Jun', nextDue: '5 Sep', waterHistory: 'clear · clear · clear', statusTone: 'success', statusLabel: 'Ok' },
]

export const FUEL_DISPENSER_ROWS: IFuelDispenserRow[] = [
  { id: 1, name: 'Pit A station', serves: 'Pit A Diesel', lastTest: '15 Jun', driftPct: 0.4, result: 'PASS', nextDue: '15 Jul', statusTone: 'success', statusLabel: 'Ok' },
  { id: 2, name: 'Workshop station', serves: 'Workshop tank', lastTest: '28 May', driftPct: 1.8, result: 'PASS', nextDue: '5 Jul · booked', statusTone: 'warning', statusLabel: 'Approaching limit' },
  { id: 3, name: 'Plant station', serves: 'Plant tank', lastTest: '10 Jun', driftPct: 0.2, result: 'PASS', nextDue: '10 Jul', statusTone: 'success', statusLabel: 'Ok' },
  { id: 4, name: 'BOWSER-01 meter', serves: 'Bowser circuits — Pit A + Pit B', lastTest: '30 Apr', driftPct: null, result: '—', nextDue: '30 May (missed)', statusTone: 'danger', statusLabel: 'Overdue 65 days', note: 'suspected in 30 Jun bowser variance breach' },
]

export const FUEL_DISPENSER_CAPTION =
  'Meter drift creates phantom consumption — monthly proving-can test per dispenser.'

export const FUEL_MONTH_END: IFuelMonthEndClose = {
  monthLabel: 'June 2026',
  openingLabel: '1 Jun',
  closingLabel: '30 Jun',
  openingL: 44730,
  purchasesL: 127300,
  issuesL: 123100,
  transfersNetL: 0,
  lossesClosedL: 580,
  closingL: 48350,
  pendingWriteOffL: 9000,
  pendingWriteOffGhs: 111600,
  memoNote: 'Memo only — not subtracted. Book stock includes 9,000 L under investigation until the GM approves the write-off.',
  continuityNote: 'Closing 48,350 L + Jul deliveries 9,000 L − Jul issues 10,396 L = 46,954 L on hand today — ties to the live Tanks total.',
}

export const FUEL_MONTH_END_TANK_ROWS: IFuelMonthEndTankRow[] = [
  { id: 9001, tank: 'Pit A Diesel', openingL: 6850, purchasesL: 33900, issuesL: 38850, transfersL: 3000, closingL: 4900 },
  { id: 9002, tank: 'Pit B Diesel', openingL: 5020, purchasesL: 3000, issuesL: 20, transfersL: 0, closingL: 8000 },
  { id: 9003, tank: 'Workshop', openingL: 17200, purchasesL: 68000, issuesL: 21000, transfersL: -49000, closingL: 15200 },
  { id: 9004, tank: 'Plant', openingL: 14180, purchasesL: 14400, issuesL: 15780, transfersL: 0, closingL: 12800 },
  { id: 9005, tank: 'Camp genset', openingL: 1230, purchasesL: 8000, issuesL: 2030, transfersL: 0, closingL: 7200 },
  { id: 9101, tank: 'BOWSER-01', openingL: 250, purchasesL: 0, issuesL: 46000, transfersL: 46000, closingL: 250 },
]

export const FUEL_CONTRACTOR_RECHARGE_ROWS: IFuelContractorRechargeRow[] = [
  { id: 1, contractor: 'Bulldog Mining Services', litres: 3240, amountGhs: 40176, status: 'statement ready' },
  { id: 2, contractor: 'GeoDrill Ltd', litres: 1120, amountGhs: 13888, status: 'statement ready' },
]
